import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ClipboardList, Plus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Receipt from '../components/Receipt';
import OrderBuilder from '../components/OrderBuilder';
import { useToast, EmptyState, PageHeader, StatusBadge, Button, FilterTabs, Input } from '../components/ui';
import { fmtMoney } from '../utils/currency';
import { connectSocket } from '../utils/socket';

const orderStatusVariant = (status) => {
  switch (status) {
    case 'completed': return 'success';
    case 'served':    return 'success';
    case 'ready':     return 'primary';
    case 'preparing': return 'warning';
    case 'cancelled': return 'danger';
    default:          return 'neutral';
  }
};

const Orders = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [paymentData, setPaymentData] = useState({ method: 'cash', amountReceived: '', tipAmount: '' });
  const [cancelTarget, setCancelTarget] = useState(null); // order awaiting cancel confirmation
  const [cancelling, setCancelling] = useState(false);
  const [riders, setRiders] = useState([]);
  const [riderPick, setRiderPick] = useState({}); // { [orderId]: riderId } — pending selection before "Assign"
  const [assigningId, setAssigningId] = useState(null);

  // Only manager and above can cancel orders
  const canCancel = ['super_admin', 'admin', 'manager'].includes(user?.role);
  const canAssignRider = ['super_admin', 'admin', 'manager', 'cashier', 'waiter'].includes(user?.role);

  useEffect(() => {
    // Live updates from other screens; this screen's own actions update
    // state directly from API responses.
    const newSocket = connectSocket();
    newSocket.emit('join-orders');

    newSocket.on('order-updated', (order) => {
      console.log('📢 Order updated:', order.orderNumber, order.status);
      setOrders(prev => prev.map(o => o._id === order._id ? order : o));
    });

    newSocket.on('payment-complete', (order) => {
      console.log('📢 Payment complete:', order.orderNumber);
      setOrders(prev => prev.map(o => o._id === order._id ? order : o));
    });

    fetchOrders();
    if (canAssignRider) fetchRiders();

    return () => {
      if (newSocket) newSocket.close();
    };
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await axios.get('/api/orders', { headers: { 'x-auth-token': localStorage.getItem('token') } });
      setOrders(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchRiders = async () => {
    try {
      const res = await axios.get('/api/riders', { headers: { 'x-auth-token': localStorage.getItem('token') } });
      setRiders(res.data);
    } catch (err) { console.error(err); }
  };

  const assignRider = async (order) => {
    const riderId = riderPick[order._id];
    if (!riderId) {
      toast.error('Select a rider first');
      return;
    }
    setAssigningId(order._id);
    try {
      const res = await axios.put('/api/riders/assign', { orderId: order._id, riderId },
        { headers: { 'x-auth-token': localStorage.getItem('token') } });
      setOrders(prev => prev.map(o => o._id === res.data._id ? res.data : o));
      toast.success(`Rider assigned to ${order.orderNumber}`);
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Error assigning rider');
    } finally {
      setAssigningId(null);
    }
  };

  // Called by the shared OrderBuilder once an order is punched in
  const handleOrderPlaced = (order) => {
    setShowCreateModal(false);
    toast.success(`Order ${order.orderNumber} placed`);
    fetchOrders();
  };

  const filteredOrders = filter === 'all' ? orders : orders.filter(o => o.status === filter);

  const updateStatus = async (id, status) => {
    try {
      const res = await axios.put(`/api/orders/${id}`, { status }, { headers: { 'x-auth-token': localStorage.getItem('token') } });
      // Update the row immediately from the API response — no reload, no
      // reliance on the socket round-trip.
      setOrders(prev => prev.map(o => o._id === res.data._id ? res.data : o));
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Error updating status');
    }
  };

  const cancelOrder = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await axios.post(`/api/orders/${cancelTarget._id}/cancel`, {}, { headers: { 'x-auth-token': localStorage.getItem('token') } });
      toast.success(`Order ${cancelTarget.orderNumber} cancelled — stock restored`);
      setCancelTarget(null);
      fetchOrders();
    } catch (err) {
      toast.error('Cancel failed: ' + (err.response?.data?.msg || err.message));
    } finally {
      setCancelling(false);
    }
  };

  const processPayment = async (orderId) => {
    try {
      const order = orders.find(o => o._id === orderId);
      const payload = {
        paymentMethod: paymentData.method,
        tipAmount: parseFloat(paymentData.tipAmount) || 0
      };

      if (paymentData.method === 'cash') {
        payload.amountReceived = parseFloat(paymentData.amountReceived) || order.totalAmount;
      }

      const res = await axios.post(`/api/orders/${orderId}/pay`, payload,
        { headers: { 'x-auth-token': localStorage.getItem('token') } });

      setCurrentOrder(res.data.order);
      setShowPayment(false);
      setShowReceipt(true);
      setPaymentData({ method: 'cash', amountReceived: '', tipAmount: '' });
      fetchOrders();
      toast.success('Payment successful');
    } catch (err) {
      toast.error('Payment failed: ' + (err.response?.data?.msg || err.message));
    }
  };

  const changeAmount = paymentData.method === 'cash' && paymentData.amountReceived && currentOrder
    ? Math.max(0, parseFloat(paymentData.amountReceived) - currentOrder.totalAmount - (parseFloat(paymentData.tipAmount) || 0))
    : 0;

  return (
    <>
      <PageHeader
        title="Orders"
        subtitle="All orders across channels — dine-in, takeaway, delivery, drive-thru."
        action={
          <Button
            variant="primary"
            leftIcon={<Plus size={16} />}
            onClick={() => setShowCreateModal(true)}
          >
            Create Order
          </Button>
        }
      />

      <div className="mb-6">
        <FilterTabs
          value={filter}
          onChange={setFilter}
          options={['all', 'pending', 'preparing', 'ready', 'completed', 'cancelled']}
        />
      </div>

      <div className="bg-white rounded-lg border border-neutral-200 shadow-card overflow-auto">
        <table className="app-table">
          <thead>
            <tr>
              <th>Order #</th>
              <th>Customer</th>
              <th>Type</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length === 0 && (
              <tr>
                <td colSpan="6">
                  <EmptyState
                    icon={ClipboardList}
                    title={filter === 'all' ? 'No orders yet' : `No ${filter} orders`}
                    message={filter === 'all'
                      ? 'Create your first order from the POS or by clicking "Create Order".'
                      : 'Try a different status filter above.'}
                  />
                </td>
              </tr>
            )}
            {filteredOrders.map(order => (
              <tr key={order._id}>
                <td className="font-mono text-xs">{order.orderNumber}</td>
                <td>{order.customer?.name || <span className="text-neutral-400">Walk-in</span>}</td>
                <td className="capitalize">{order.orderType}</td>
                <td className="font-medium">{fmtMoney(order.totalAmount)}</td>
                <td>
                  <StatusBadge variant={orderStatusVariant(order.status)} dot>
                    {order.status}
                  </StatusBadge>
                </td>
                <td className="min-w-[200px]">
                  <div className="mb-1.5 text-xs text-neutral-500">
                    {order.status === 'pending' && 'Waiting for KDS'}
                    {order.status === 'preparing' && 'In Kitchen'}
                    {order.status === 'ready' && 'Ready for service'}
                    {order.status === 'served' && 'Ready to pay'}
                    {order.status === 'completed' && 'Completed'}
                  </div>
                  {order.paymentStatus !== 'paid' && (
                    <div className="flex flex-wrap items-center gap-1.5">
                      {/* Helper: Check if user can serve/pickup */}
                      {(() => {
                        const canServe = user?.role === 'waiter' || user?.role === 'cashier' || user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'manager';
                        const canPickup = user?.role === 'cashier' || user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'manager';
                        const canPay = user?.role === 'cashier' || user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'manager';

                        return (
                          <>
                            {/* DINE-IN: Ready → Waiter/Cashier/Admin clicks "Serve" → Then pay */}
                            {order.orderType === 'dine-in' && order.status === 'ready' && canServe && (
                              <Button size="sm" variant="primary" onClick={() => updateStatus(order._id, 'served')}>
                                Serve ({fmtMoney(order.totalAmount)})
                              </Button>
                            )}
                            {order.orderType === 'dine-in' && order.status === 'served' && canPay && (
                              <Button size="sm" variant="success" onClick={() => { setCurrentOrder(order); setShowPayment(true); }}>
                                Pay Now ({fmtMoney(order.totalAmount)})
                              </Button>
                            )}

                            {/* TAKEAWAY: Ready → Cashier/Admin clicks "Pickup" → Then pay */}
                            {order.orderType === 'takeaway' && order.status === 'ready' && canPickup && (
                              <Button size="sm" variant="primary" onClick={() => updateStatus(order._id, 'served')}>
                                Pickup ({fmtMoney(order.totalAmount)})
                              </Button>
                            )}
                            {order.orderType === 'takeaway' && order.status === 'served' && canPay && (
                              <Button size="sm" variant="success" onClick={() => { setCurrentOrder(order); setShowPayment(true); }}>
                                Pay & Complete
                              </Button>
                            )}

                            {/* DELIVERY: assign a rider, then wait for them to deliver */}
                            {order.orderType === 'delivery' && !order.rider && canAssignRider && (
                              <div className="flex flex-wrap items-center gap-1.5">
                                <select
                                  value={riderPick[order._id] || ''}
                                  onChange={(e) => setRiderPick(prev => ({ ...prev, [order._id]: e.target.value }))}
                                  className="border border-neutral-300 rounded-md bg-white px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-500"
                                >
                                  <option value="">Select rider…</option>
                                  {riders.map(r => (
                                    <option key={r._id} value={r._id}>{r.name}</option>
                                  ))}
                                </select>
                                <Button size="sm" variant="secondary" loading={assigningId === order._id} onClick={() => assignRider(order)}>
                                  Assign
                                </Button>
                              </div>
                            )}
                            {order.orderType === 'delivery' && !order.rider && !canAssignRider && (
                              <span className="text-sm text-neutral-500">Waiting for rider assignment</span>
                            )}
                            {order.orderType === 'delivery' && order.rider && order.status === 'ready' && (
                              <span className="text-sm text-neutral-500">
                                Out with <span className="font-medium">{order.rider?.name || 'rider'}</span>
                              </span>
                            )}
                            {order.orderType === 'delivery' && order.status === 'served' && (
                              <span className="text-sm text-warning-600">COD - Collect on delivery</span>
                            )}

                            {/* DRIVE-THRU: Ready → Cashier/Admin clicks "Ready to Pay" → Then pay at window */}
                            {order.orderType === 'drive-thru' && order.status === 'ready' && canPickup && (
                              <Button size="sm" variant="primary" onClick={() => updateStatus(order._id, 'served')}>
                                Ready to Pay
                              </Button>
                            )}
                            {order.orderType === 'drive-thru' && order.status === 'served' && canPay && (
                              <Button size="sm" variant="success" onClick={() => { setCurrentOrder(order); setShowPayment(true); }}>
                                Pay at Window
                              </Button>
                            )}
                          </>
                        );
                      })()}
                      {canCancel && order.status !== 'cancelled' && (
                        <Button size="sm" variant="ghost" onClick={() => setCancelTarget(order)}>
                          Cancel Order
                        </Button>
                      )}
                    </div>
                  )}
                  {order.paymentStatus === 'paid' && (
                    <span className="text-success-600 text-sm font-semibold">✓ Paid</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create order — the same full ordering surface as the POS (add-ons,
          spice, notes, customer, stock guards, search + category filter),
          shared via OrderBuilder. Payment stays out of this page for waiters. */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 overflow-y-auto p-4 sm:p-8">
          <div className="bg-neutral-50 rounded-lg p-6 w-full max-w-6xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-neutral-900">Create New Order</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-neutral-400 hover:text-neutral-700 text-2xl leading-none"
                title="Close"
              >
                ×
              </button>
            </div>
            <OrderBuilder onPlaced={handleOrderPlaced} />
          </div>
        </div>
      )}

      {showPayment && currentOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 sm:p-8 rounded-lg shadow-card-hover max-w-md w-full">
            <h2 className="text-xl sm:text-2xl font-bold mb-6 text-neutral-900">Process Payment</h2>
            <div className="mb-4">
              <p className="text-neutral-600 mb-2">Order: <span className="font-bold text-neutral-900">{currentOrder.orderNumber}</span></p>
              <p className="text-neutral-600 mb-2">Total: <span className="font-bold text-neutral-900">{fmtMoney(currentOrder.totalAmount, true)}</span></p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-neutral-700">Payment Method</label>
              <div className="grid grid-cols-3 gap-2">
                {['cash', 'card', 'digital_wallet'].map(method => (
                  <button key={method} type="button" onClick={() => setPaymentData({ ...paymentData, method })}
                    className={`p-3 rounded-md border text-sm font-medium transition-colors ${paymentData.method === method ? 'border-primary-500 bg-primary-50 text-primary-700 ring-2 ring-primary-200' : 'border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300'}`}>
                    {method === 'digital_wallet' ? 'Digital' : method.charAt(0).toUpperCase() + method.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {paymentData.method === 'cash' && (
              <Input
                label="Amount received"
                type="number"
                value={paymentData.amountReceived}
                onChange={(e) => setPaymentData({ ...paymentData, amountReceived: e.target.value })}
                placeholder={currentOrder.totalAmount.toFixed(2)}
                helper={changeAmount > 0 ? `Change to give: ${fmtMoney(changeAmount, true)}` : undefined}
              />
            )}

            <div className="mt-4 mb-2">
              <Input
                label="Tip (optional)"
                type="number"
                value={paymentData.tipAmount}
                onChange={(e) => setPaymentData({ ...paymentData, tipAmount: e.target.value })}
                placeholder="0.00"
              />
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="secondary"
                fullWidth
                onClick={() => { setShowPayment(false); setCurrentOrder(null); setPaymentData({ method: 'cash', amountReceived: '', tipAmount: '' }); }}
              >
                Cancel
              </Button>
              <Button variant="success" fullWidth onClick={() => processPayment(currentOrder._id)}>
                Confirm Payment
              </Button>
            </div>
          </div>
        </div>
      )}

      {cancelTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-card-hover max-w-sm w-full p-6">
            <h2 className="text-xl font-bold text-neutral-900 mb-2">Cancel this order?</h2>
            <p className="text-sm text-neutral-600 mb-1">
              Order <span className="font-mono font-semibold">{cancelTarget.orderNumber}</span> · {fmtMoney(cancelTarget.totalAmount)}
            </p>
            <p className="text-sm text-neutral-600 mb-6">
              The ingredients will be returned to inventory and any table will be freed. This can’t be undone.
            </p>
            <div className="flex gap-3">
              <Button variant="secondary" fullWidth disabled={cancelling} onClick={() => setCancelTarget(null)}>
                Keep order
              </Button>
              <Button variant="danger" fullWidth loading={cancelling} onClick={cancelOrder}>
                Yes, cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {showReceipt && <Receipt order={currentOrder} onClose={() => { setShowReceipt(false); setCurrentOrder(null); }} />}
    </>
  );
};

export default Orders;
